<template>
  <article>
    <div class="border-1 border-grey-100 rounded px-5 md:px-8 py-6">
      <h3
        class="text-red uppercase font-roboto-condensed font-bold text-lg md:text-xl mb-6"
      >
        Finden Sie Ihren Händler-Partner ganz in Ihrer Nähe!
      </h3>

      <div class="flex flex-wrap -mx-4 -my-3">
        <div
          class="px-4 py-3 w-full text-center lg:text-left"
          :class="{
            'lg:w-3/4': showFilters,
            'lg:w-2/3': !showFilters,
          }"
        >
          <div class="flex flex-wrap -mx-4 -my-3">
            <div
              class="space-y-2 px-4 py-3 w-full"
              :class="{
                'lg:w-1/2': showFilters,
              }"
            >
              <div class="flex">
                <label
                  class="text-red font-bold font-roboto-condensed text-xs uppercase tracking-px"
                  for="input-ort-in-oesterreich"
                  >Ort in Österreich</label
                >
              </div>
              <gmap-autocomplete
                ref="search"
                :value="value"
                :component-restrictions="{ country: ['at'] }"
                :options="{
                  types: ['geocode'],
                }"
                type="search"
                placeholder="Ort, Straße oder PLZ …"
                name="place"
                @place_changed="placeChanged"
              />
              <button
                type="button"
                class="button-sm mt-4 lg:mt-3 text-grey-200"
                @click="getGeoLocation"
              >
                Meinen Standort verwenden
              </button>
            </div>
            <div v-if="showFilters" class="px-4 py-3 w-full lg:w-1/2 text-left">
              <fieldset class="flex">
                <legend
                  class="text-red font-bold font-roboto-condensed text-xs uppercase tracking-px"
                >
                  Filter
                </legend>
              </fieldset>
              <label class="checkbox mt-2 lg:mt-5">
                <input
                  type="checkbox"
                  name="llkw"
                  id="filter-lkw"
                  v-model="filterLkw"
                />
                <svg width="22" height="22" viewBox="0 0 22 22">
                  <rect
                    x="4"
                    y="4"
                    width="15"
                    height="15"
                    rx="2"
                    ry="2"
                    fill="rgba(73,80,87,0.05)"
                    stroke-width="1"
                    stroke="rgba(73,80,87,0.1)"
                  />
                  <rect
                    x="1.5"
                    y="1.5"
                    width="19"
                    height="19"
                    rx="4"
                    ry="4"
                    stroke="rgba(227,23,33,0.25)"
                    stroke-width="3"
                    fill="none"
                    class="visible-when-checked"
                  />
                  <g
                    transform="matrix(1,0,0,1,7,7)"
                    class="visible-when-checked"
                  >
                    <path
                      d="M0,4.6L2.286,7L8,1"
                      style="
                        fill: none;
                        fill-rule: nonzero;
                        stroke: rgb(227, 23, 33);
                        stroke-width: 1.5px;
                      "
                    />
                  </g></svg
                ><span>Nur LKW-Reifenhändler anzeigen</span>
              </label>
            </div>
          </div>
        </div>

        <div class="px-4 py-3 w-full lg:w-1/4 ml-auto text-right">
          <button
            type="submit"
            class="button-lg w-full lg:mt-7"
            @click="clickSearch"
          >
            Suchen
          </button>

          <button
            type="button"
            class="button-sm mt-4 lg:mt-3"
            @click="mapIsVisible = !mapIsVisible"
          >
            Karte ausblenden
          </button>
        </div>
      </div>

      <div
        class="h-100 mt-8 rounded-lg overflow-hidden border-1 border-grey-100"
        v-if="mapIsVisible"
        id="map"
      >
        <maps-component
          :dealers="computedDealers"
          :pinnedAddress="pinnedAddress"
          :activeDealer="activeDealer"
          @setActiveDealer="setActiveDealer"
          @closeInfoWindow="closeInfoWindow"
        ></maps-component>
      </div>
    </div>

    <div class="md:flex md:px-8 text-xs mt-12">
      <div class="mb-4 md:mb-0 text-grey-200">
        <strong>{{ computedDealers.length }}</strong> Händler verfügbar
      </div>
      <div class="ml-auto" v-if="!pinnedAddress">
        <fieldset class="flex space-x-4">
          <legend class="mr-auto float-left text-grey-200">
            Alphabetisch sortieren
          </legend>

          <label class="radio-icon">
            <input type="radio" value="asc" name="sort" v-model="sort" />
            <span class="radio-icon-inner">
              <svg width="16" height="17" aria-hidden="true" focusable="false">
                <use xlink:href="#sort-asc" />
              </svg>
            </span>
            <span class="sr-only">Aufsteigend</span>
          </label>
          <label class="radio-icon">
            <input type="radio" value="desc" name="sort" v-model="sort" />
            <span class="radio-icon-inner">
              <svg width="16" height="17" aria-hidden="true" focusable="false">
                <use xlink:href="#sort-desc" />
              </svg>
            </span>
            <span class="sr-only">Absteigend</span>
          </label>
        </fieldset>
      </div>
    </div>
    <ul v-if="dealers.length" class="space-y-6 mt-6">
      <dealer-component
        v-for="dealer in computedDealers"
        :key="dealer.haendlernummer"
        :dealer="dealer"
        @setActiveDealer="setActiveDealer"
        @showMap="mapIsVisible = true"
      ></dealer-component>
    </ul>
  </article>
</template>

<script>
import DealerComponent from './DealerComponent.vue';
import MapsComponent from './MapsComponent.vue';
import { addDistance, googleIsLoaded } from '../tools';

export default {
  components: {
    DealerComponent,
    MapsComponent,
  },

  props: {
    dealers: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      activeDealer: null,
      pinnedAddress: null,
      value: '',
      sort: 'asc',
      mapIsVisible: true,
      filterLkw: false,
    };
  },

  computed: {
    sortedDealers() {
      if (!this.pinnedAddress) {
        return [...this.dealers].sort((a, b) => {
          return (
            a.name1.localeCompare(b.name1) * (this.sort === 'asc' ? 1 : -1)
          );
        });
      }

      return this.dealers
        .map(dealer => ({
          ...dealer,
          location: { lat: dealer.latitude, lng: dealer.longitude },
        }))
        .map(dealer => addDistance(dealer, this.pinnedAddress))
        .sort((a, b) => {
          if (a.distance && b.distance) {
            return a.distance - b.distance;
          }

          // Puts dealers without lat && lng to bottom of list.
          return a.distance ? -1 : 1;
        });
    },

    computedDealers() {
      if (!this.filterLkw) {
        return this.sortedDealers;
      }
      return this.sortedDealers.filter(dealer => dealer.flotte_lkw);
    },

    showFilters() {
      return this.dealers.some(dealer => dealer.flotte_lkw);
    },
  },

  methods: {
    setActiveDealer(value) {
      this.activeDealer = value;
    },
    closeInfoWindow() {
      this.activeDealer = null;
    },
    clickSearch() {
      this.geocode({ address: this.$refs.search.$refs.input.value });
    },

    clear() {
      window.history.replaceState(
        null,
        document.title,
        `${document.location.origin}${document.location.pathname}`,
      );

      this.pinnedAddress = null;
      this.warning = null;
      this.value = null;
    },

    getGeoLocation() {
      this.clear();
      this.fetchingLocation = true;

      navigator.geolocation.getCurrentPosition(position => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        googleIsLoaded().then(() => {
          this.geocode({ location: pos }, false).then(() => {
            this.fetchingLocation = false;
          });
        });
      });
    },

    setValue(value) {
      if (value && value.geometry) {
        this.value = value.formatted_address;
        this.pinnedAddress = value;
      }
    },

    geocode(location, restrict = true) {
      return new Promise(resolve => {
        const request = {
          ...location,
        };

        if (restrict) {
          request.componentRestrictions = {
            country: 'AT',
          };
        }

        this.clear();

        this.geocoder = new google.maps.Geocoder();
        this.geocoder.geocode(request, (results, status) => {
          if (status === 'OK') {
            const bestResult = results[0];
            this.setValue(bestResult);

            if (bestResult.partial_match) {
              this.warning = `Kein eindeutiges Ergebnis für <span class="font-bold capitalize">
                ${request.address}</span> gefunden.`;
            }
            resolve();
          }

          if (status === 'ZERO_RESULTS') {
            this.warning = `Keine Ergebnisse für <span class="font-bold capitalize">
              ${request.address}</span> gefunden.`;
          }
        });
      });
    },

    placeChanged(place) {
      if (place && place.name) {
        this.geocode({ address: place.name });
      }
    },
  },
};
</script>
